import React, { useEffect, useState } from 'react';
import { Drawer, Table, Button, Tag, Typography, Space, Empty, message } from 'antd';
import { DownloadOutlined, HistoryOutlined } from '@ant-design/icons';
import { listArchivedDocs, getArchivedDocDownloadUrl } from 'api/documentosArchivados';

const TIPO_ESTADO_COLOR = {
  enviado: 'blue',
  entregado: 'green',
  rebotado: 'red',
  abierto: 'gold',
};

// Historial inmutable de versiones entregadas de un documento (mantención o comprobante).
function DocumentDeliveryDrawer({ open, onClose, tipo, entidadId, titulo }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!open || !tipo || !entidadId) return;
    setRows([]);
    setLoading(true);
    listArchivedDocs(tipo, entidadId)
      .then((r) => setRows(r))
      .catch(() => message.error('No se pudo cargar el historial de entregas'))
      .finally(() => setLoading(false));
  }, [open, tipo, entidadId]);

  const onDownload = async (id) => {
    setDownloadingId(id);
    try {
      const url = await getArchivedDocDownloadUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch {
      message.error('No se pudo generar el enlace de descarga');
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    { title: 'Versión', dataIndex: 'version', key: 'version', width: 90, render: (v) => `v${v}` },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => (d ? new Date(d).toLocaleString('es-CL') : '—'),
    },
    {
      title: 'Huella (sha256)',
      dataIndex: 'sha256',
      key: 'sha256',
      render: (s) => (s ? <Typography.Text code>{s.slice(0, 10)}…</Typography.Text> : '—'),
    },
    {
      title: 'Entregas',
      key: 'entregas',
      render: (_t, row) => row.entregas?.length ?? 0,
    },
    {
      title: '',
      key: 'acciones',
      width: 140,
      render: (_t, row) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={downloadingId === row.id}
          onClick={() => onDownload(row.id)}
        >
          Descargar
        </Button>
      ),
    },
  ];

  const expandedRowRender = (row) => (
    <div style={{ paddingLeft: 8 }}>
      {(row.entregas || []).length === 0 ? (
        <Typography.Text type="secondary">Sin entregas registradas.</Typography.Text>
      ) : (
        row.entregas.map((e) => (
          <div key={e.id} style={{ marginBottom: 8 }}>
            <Typography.Text strong>
              {e.enviado_at ? new Date(e.enviado_at).toLocaleString('es-CL') : ''} —{' '}
              {e.asunto || ''}
            </Typography.Text>
            <div>
              <Space size={[4, 4]} wrap>
                {(e.destinatarios || []).map((d) => (
                  <Tag key={d.email + d.tipo} color={TIPO_ESTADO_COLOR[d.estado] || 'default'}>
                    {d.tipo?.toUpperCase()}: {d.email} · {d.estado}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Historial de entregas{titulo ? ` — ${titulo}` : ''}</span>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={640}
      destroyOnHidden
    >
      {rows.length === 0 && !loading ? (
        <Empty description="Aún no hay versiones entregadas" />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          expandable={{ expandedRowRender }}
          size="small"
        />
      )}
    </Drawer>
  );
}

export default DocumentDeliveryDrawer;
